import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import ActionsMenu from "../../components/ActionsMenu";
import BarData from "../../components/Charts/BarData";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialUnderContractFilterData } from "../../utils/initialData";
import ExecutedFilter from "../Executed LOI/component/ExecutedFilter";
import LeadDataContainer from "../Leads/components/LeadDataContainer";
import AddUnderContractModal from "./AddUnderContractModal";
import ConvertClosedModal from "./components/ConvertClosedModal";
import FellOutContract from "./components/FellOutContract";
import EditUnderContractData from "./EditUnderContractModal";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const UnderContract = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [expandedRows, setExpandedRows] = useState({});
  const [sortDirection, setSortDirection] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showfelloutcontract, setShowFellOutContract] = useState(false);
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [undercontractDashboard, setUnderContractDashboard] = useState();
  const queryParams = new URLSearchParams(location.search);

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialUnderContractFilterData;
  });

  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const [filterStatus, setFilterStatus] = useState({
    new_under_contract_quarter: false,
    total_under_contract_transaction: false,
    under_contract_buyer_transaction: false,
    under_contract_seller_transaction: false,
    under_contrac_gcl_to_company_ytd: false,
    avg_commission_per_transaction_ytd: false,
    buyer_initial_offer_to_under_contract_conversion_rate: false,
    exclusive_under_contract_conversion: false,
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  // ==================== Existing Logic ====================
  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const {
      created_at,
      property_name,
      noi,
      created_end_date,
      created_start_date,
      min_contract_price,
      max_contract_price,
      min_contract_cap_rate,
      max_contract_cap_rate,
      max_asking_price,
      min_asking_price,
      max_potential_gcl,
      avg_commission_per_transaction_ytd,
      exclusive_under_contract_conversion,
      buyer_initial_offer_to_under_contract_conversion_rate,
      total_under_contract_transaction,
      new_under_contract_quarter,
      under_contract_seller_transaction,
      under_contract_buyer_transaction,
      seller_initial_offer_to_under_contract_conversion_rate,
      broker,
      under_contrac_gcl_to_company_ytd,
      jan,
      feb,
      mar,
      apr,
      may,
      jun,
      jul,
      aug,
      sep,
      oct,
      nov,
      dec,
      gci_closing_quarter1,
      gci_closing_quarter2,
      gci_closing_quarter3,
      gci_closing_quarter4
    } = filters;

    if (created_at) {
      params += `&created_at=${encodeURIComponent(created_at)}`;
    }
    if (created_start_date) {
      params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    }
    if (created_end_date) {
      params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    }
    if (property_name) {
      params += `&property_name=${encodeURIComponent(property_name)}`;
    }
    if (noi) {
      params += `&noi=${encodeURIComponent(noi)}`;
    }
    if (broker) {
      params += `&broker=${encodeURIComponent(broker)}`;
    }
    if (min_contract_price) {
      params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    }
    if (max_contract_price) {
      params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    }
    if (min_contract_cap_rate) {
      params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    }
    if (max_contract_cap_rate) {
      params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    }
    if (min_asking_price) {
      params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    }
    if (max_asking_price) {
      params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;
    }
    if (avg_commission_per_transaction_ytd) {
      params += `&avg_commission_per_transaction_ytd=true`;
    }
    if (buyer_initial_offer_to_under_contract_conversion_rate) {
      params += `&buyer_initial_offer_to_under_contract_conversion_rate=true`;
    }
    if (exclusive_under_contract_conversion) {
      params += `&exclusive_under_contract_conversion=true`;
    }
    if (new_under_contract_quarter) {
      params += `&new_under_contract_quarter=true`;
    }
    if (seller_initial_offer_to_under_contract_conversion_rate) {
      params += `&seller_initial_offer_to_under_contract_conversion_rate=true`;
    }
    if (total_under_contract_transaction) {
      params += `&total_under_contract_transaction=true`;
    }
    if (under_contrac_gcl_to_company_ytd) {
      params += `&under_contrac_gcl_to_company_ytd=true`;
    }
    if (under_contract_seller_transaction) {
      params += `&under_contract_seller_transaction=true`;
    }
    if (under_contract_buyer_transaction) {
      params += `&under_contract_buyer_transaction=true`;
    }
    if (jan) {
      params += `&jan=true`;
    }
    if (feb) {
      params += `&feb=true`;
    }
    if (mar) {
      params += `&mar=true`;
    }
    if (apr) {
      params += `&apr=true`;
    }
    if (may) {
      params += `&may=true`;
    }
    if (jun) {
      params += `&jun=true`;
    }
    if (jul) {
      params += `&jul=true`;
    }
    if (aug) {
      params += `&aug=true`;
    }
    if (sep) {
      params += `&sep=true`;
    }
    if (oct) {
      params += `&oct=true`;
    }
    if (nov) {
      params += `&nov=true`;
    }
    if (dec) {
      params += `&dec=true`;
    }
    if (gci_closing_quarter1) {
      params += `&gci_closing_quarter1=true`;
    }
    if (gci_closing_quarter2) {
      params += `&gci_closing_quarter2=true`;
    }
    if (gci_closing_quarter3) {
      params += `&gci_closing_quarter3=true`;
    }
    if (gci_closing_quarter4) {
      params += `&gci_closing_quarter4=true`;
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    return params;
  };

  const fetchData = (filters) => {
    const queryParams = buildQueryParams({
      ...filters,
      new_under_contract_quarter: filterStatus.new_under_contract_quarter,
      total_under_contract_transaction: filterStatus.total_under_contract_transaction,
      under_contract_buyer_transaction: filterStatus.under_contract_buyer_transaction,
      under_contract_seller_transaction: filterStatus.under_contract_seller_transaction,
      under_contrac_gcl_to_company_ytd: filterStatus.under_contrac_gcl_to_company_ytd,
      avg_commission_per_transaction_ytd: filterStatus.avg_commission_per_transaction_ytd,
      buyer_initial_offer_to_under_contract_conversion_rate: filterStatus.buyer_initial_offer_to_under_contract_conversion_rate,
      exclusive_under_contract_conversion: filterStatus.exclusive_under_contract_conversion,
      jan: filterStatus.jan,
      feb: filterStatus.feb,
      mar: filterStatus.mar,
      apr: filterStatus.apr,
      may: filterStatus.may,
      jun: filterStatus.jun,
      jul: filterStatus.jul,
      aug: filterStatus.aug,
      sep: filterStatus.sep,
      oct: filterStatus.oct,
      nov: filterStatus.nov,
      dec: filterStatus.dec,
      gci_closing_quarter1: filterStatus.gci_closing_quarter1,
      gci_closing_quarter2: filterStatus.gci_closing_quarter2,
      gci_closing_quarter3: filterStatus.gci_closing_quarter3,
      gci_closing_quarter4: filterStatus.gci_closing_quarter4,
    });
    axios
      .get(`${BASE_URL}/contract-listing?active_tab=${activeTab}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.contract;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(filterData);
  }, [sortDirection, sortBy, filterStatus]);

  useEffect(() => {
    onSuccess();
  }, [search, currentPage, itemPerPage, activeTab]);

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchData(filters);
    } else {
      fetchData(filterData);
    }
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-contract/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const handleConfirm = () => {
    axios
      .post(`${BASE_URL}/mark-contract/${selectedId}`, { mark_contract: true }, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowConfirmModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const BarGraphData = [
    { name: "Q1", count: undercontractDashboard?.gci_closing_quarter1, key: "gci_closing_quarter1" },
    { name: "Q2", count: undercontractDashboard?.gci_closing_quarter2, key: "gci_closing_quarter2" },
    { name: "Q3", count: undercontractDashboard?.gci_closing_quarter3, key: "gci_closing_quarter3" },
    { name: "Q4", count: undercontractDashboard?.gci_closing_quarter4, key: "gci_closing_quarter4" },
  ];

  const BarGraphData2 = [
    { name: "Jan", count: undercontractDashboard?.Jan, key: "jan" },
    { name: "Feb", count: undercontractDashboard?.Feb, key: "feb" },
    { name: "Mar", count: undercontractDashboard?.Mar, key: "mar" },
    { name: "Apr", count: undercontractDashboard?.Apr, key: "apr" },
    { name: "May", count: undercontractDashboard?.May, key: "may" },
    { name: "Jun", count: undercontractDashboard?.Jun, key: "jun" },
    { name: "Jul", count: undercontractDashboard?.Jul, key: "jul" },
    { name: "Aug", count: undercontractDashboard?.Aug, key: "aug" },
    { name: "Sep", count: undercontractDashboard?.Sept, key: "sep" },
    { name: "Oct", count: undercontractDashboard?.Oct, key: "oct" },
    { name: "Nov", count: undercontractDashboard?.Nov, key: "nov" },
    { name: "Dec", count: undercontractDashboard?.Dec, key: "dec" },
  ];

  const fetchDashboardData = () => {
    setLoading(true);

    axios
      .get(`${BASE_URL}/dashboard-under-contract?active_tab=${activeTab}`, config)
      .then((res) => {
        setUnderContractDashboard(res?.data?.under_contract_dashboard);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboardData();
  }, [activeTab]);

  const handleBoxClick = (key) => {
    const filterKey = typeof key === "string" ? key : key;
    let targetTab = activeTab;

    if (activeTab === 0) {
      targetTab = 1;
    } else if (activeTab === 2) {
      targetTab = 3;
    }

    const newFilterStatus = {
      new_under_contract_quarter: false,
      total_under_contract_transaction: false,
      under_contract_buyer_transaction: false,
      under_contract_seller_transaction: false,
      under_contrac_gcl_to_company_ytd: false,
      avg_commission_per_transaction_ytd: false,
      buyer_initial_offer_to_under_contract_conversion_rate: false,
      exclusive_under_contract_conversion: false,
      gci_closing_quarter1: false,
      gci_closing_quarter2: false,
      gci_closing_quarter3: false,
      gci_closing_quarter4: false,
      jan: false,
      feb: false,
      mar: false,
      apr: false,
      may: false,
      jun: false,
      jul: false,
      aug: false,
      sep: false,
      oct: false,
      nov: false,
      dec: false,
    };
    if (typeof key === "string") {
      newFilterStatus[key] = true;
    } else {
      Object.keys(key).forEach((k) => {
        if (key[k] === true) {
          newFilterStatus[k] = true;
        }
      });
    }

    setFilterStatus(newFilterStatus);
    setActiveTab(targetTab);
    setCurrentPage(1);
  };

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Under Contract</p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">
            <ExecutedFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              from="Under Contract"
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative">
              <input
                type="text"
                className="body-S"
                placeholder="Search name, address, city, state"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>
            <button className="add-contact-button green-bg-H light-L body-S" onClick={() => setShowAddModal(true)}>
              <img className="mr-2 sidebar-icons" src={Plus || "/placeholder.svg"} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {["My Dashboard", "My Under Contract", "Team Dashboard", "Team Under Contract"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
              onClick={() => {
                setActiveTab(index)
                setCurrentPage(1)
                setFilterStatus({
                  new_under_contract_quarter: false,
                  total_under_contract_transaction: false,
                  under_contract_buyer_transaction: false,
                  under_contract_seller_transaction: false,
                  under_contrac_gcl_to_company_ytd: false,
                  avg_commission_per_transaction_ytd: false,
                  buyer_initial_offer_to_under_contract_conversion_rate: false,
                  exclusive_under_contract_conversion: false,
                })
              }}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {(activeTab === 1 || activeTab === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : listData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">

                    <th>
                      <p>Details</p>
                    </th>

                    <th className="green-H">
                      <div className="table-header">
                        Contract Date
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("created_at")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        PROPERTY NAME
                        <img
                          role="button"
                          src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("property_name")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        NOI
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("created_at")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        CONTRACT PRICE
                        <img
                          role="button"
                          src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("contract_price")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        CONTRACT CAP RATE
                        <img
                          role="button"
                          src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("contract_cap_rate")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        % ASKING PRICE
                        <img
                          role="button"
                          src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("asking_price")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    {/* <th>
                      <div className="table-header">
                        % OF ASKING PRICE
                        <img
                          role="button"
                          src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("asking_price")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th> */}

                    <th>
                      <div className="table-header">
                        GCI TO COMPANY
                        <img
                          role="button"
                          src={sortBy !== "company_gcl" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("company_gcl")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">Actions</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {listData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">

                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="icon"
                            className="sidebar-icons"
                            onClick={() => toggleRowDetails(el.id)}
                          />
                        </td>

                        <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>

                        <td className="head-5 capitalize">
                          <Link to={
                            el?.portfolio === 0
                              ? `/property/${el?.executed?.property_id}`
                              : el?.executed?.link?.owner_info_type === "contact"
                                ? `/contact/${el?.executed?.link?.owner?.id}`
                                : `/company/${el?.executed?.link?.owner?.id}`
                          }
                            onClick={(e) => {
                              e.preventDefault();

                              if (el?.portfolio === 0) {
                                navigate(`/property/${el?.executed?.property_id}`);
                              } else if (el?.executed?.link?.owner_info_type === "contact") {
                                navigate(`/contact/${el?.executed?.link?.owner?.id}`);
                              } else if (el?.executed?.link?.owner_info_type === "company") {
                                navigate(`/company/${el?.executed?.link?.owner?.id}`);
                              }
                            }}>
                            {el?.executed?.link?.property_name} <br />
                            {el?.executed?.link?.street_address} <br />
                            {el?.executed?.link?.city && el?.executed?.link?.state
                              ? `${el.executed.link.city}, ${el.executed.link.state}`
                              : ""}

                          </Link>
                        </td>
                        <td className="text-center">${el?.executed?.link?.noi_format}</td>
                        <td className="text-center">${el?.contract_price_format}</td>
                        <td className="text-center">
                          <p className=" capitalize">{el?.contract_cap_rate_format}%</p>
                        </td>
                        <td className="text-center">{el?.asking_price_format_percentage}%</td>
                        {/* <td className="text-center">{el?.asking_price_format_percentage}%</td> */}
                        <td className="text-center">${el?.executed?.gross_commission?.gci_to_company}</td>
                        <td className="flex gap-3 items-center w-full justify-center">
                          <ActionsMenu
                            from="under-contract"
                            handleEdit={() => {
                              setShowEditModal(true)
                              setSelectedId(el?.id)
                            }}
                            handleDelete={() => {
                              setSelectedId(el?.id)
                              setShowDeleteModal(true)
                            }}
                            showOtherOption={true}
                            otherOptionTitle={"Convert to Closed"}
                            handleOtherOption={() => {
                              setShowConfirmModal(true)
                              setSelectedId(el?.id)
                            }}
                            felloutcontractoption={"Fell Out Of Contract"}
                            handlefelloutcontractOption={() => {
                              setShowFellOutContract(true)
                              setSelectedId(el?.id)
                            }}
                          />
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-6 items-start">
                                  {/* Acquisition */}
                                  <div>
                                    <p className="dark-H head-4">Acquisition</p>
                                    <Link
                                      to={`/contact/${el?.executed?.offer?.buyer_contact_id}`}
                                      className="body-N dark-H"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        navigate(`/contact/${el?.executed?.offer?.buyer_contact_id}`)
                                      }}
                                    >
                                      {el?.executed?.offer?.acquistion_name || ""}
                                    </Link>
                                  </div>

                                  {/* Disposition */}
                                  <div>
                                    <p className="dark-H head-4">Disposition</p>
                                    <Link
                                      to={`/contact/${el?.executed?.offer?.seller_contact_id}`}
                                      className="body-N dark-H"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        navigate(`/contact/${el?.executed?.offer?.seller_contact_id}`)
                                      }}
                                    >
                                      {el?.executed?.offer?.disposition_name || ""}
                                    </Link>
                                  </div>

                                  {/* Asking Cap Rate */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Cap Rate</p>
                                    <p className="body-N dark-H">
                                      {el?.link?.asking_cap_rate_format ? `${el?.link?.asking_cap_rate_format}%` : "0"}
                                    </p>
                                  </div>

                                  {/* Asking Price */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Price</p>
                                    <p className="body-N dark-H">
                                      {el?.link?.asking_price_format ? `$${el?.link?.asking_price_format}` : "0"}
                                    </p>
                                  </div>

                                  {/* Seller Broker */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.executed?.offer?.seller_broker?.length > 0 ? (
                                        el.executed.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.executed.offer.seller_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Seller BDS */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.executed?.offer?.seller_bds?.first_name} {el?.executed?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  {/* Buyer Broker */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.executed?.offer?.buyer_broker?.length > 0 ? (
                                        el.executed.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Buyer BDS */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.executed?.offer?.buyer_bds?.first_name} {el?.executed?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>
                                </div>


                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.executed?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.executed?.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share}%)
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.executed?.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share}%)
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ContactPagination
            paginationData={paginationData}
            activeTab={activeTab}
            handleNext={handleNext}
            handlePrev={handlePrev}
            onSuccess={onSuccess}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value)
              setCurrentPage(1)
            }}
          />
        </div>
      )}

      {(activeTab === 0 || activeTab === 2) && (
        <>
          <div className="flex gap-3">
            <div className="w-[70%]">
              <div className="flex items-center gap-3">
                <LeadDataContainer
                  text="NEW UNDER CONTRACT THIS QUARTER"
                  number={undercontractDashboard?.new_under_contract_quarter}
                  onClick={() => handleBoxClick("new_under_contract_quarter")}
                />
                <LeadDataContainer
                  text="TOTAL TRANSACTIONS UNDER CONTRACT YTD"
                  number={undercontractDashboard?.total_under_contract_transaction}
                  onClick={() => handleBoxClick("total_under_contract_transaction")}
                />
                <LeadDataContainer
                  text="BUYER TRANSACTIONS UNDER CONTRACT YTD"
                  number={undercontractDashboard?.under_contract_buyer_transaction}
                  onClick={() => handleBoxClick("under_contract_buyer_transaction")}
                />
                <LeadDataContainer
                  text="SELLER TRANSACTIONS UNDER CONTRACT YTD"
                  number={undercontractDashboard?.under_contract_seller_transaction}
                  onClick={() => handleBoxClick("under_contract_seller_transaction")}
                />
              </div>
              <div className="flex gap-4 items-center">
                <BarData Title="GROSS HOPPER GAIN BY QUARTER"
                  BarGraphData={BarGraphData}
                  className="w-[50%]"
                  onBarClick={(key) => handleBoxClick(key)} />

                <BarData
                  className="w-[50%]"
                  Title="GCI CLOSING PROJECTIONS"
                  BarGraphData={BarGraphData2}
                  onBarClick={(key) => handleBoxClick(key)}
                />
              </div>
            </div>

            <div className="w-[30%] ">
              <LeadDataContainer
                text="UNDER CONTRACT GCI TO COMPANY"
                number={`$${undercontractDashboard?.under_contrac_gcl_to_company_ytd ?? 0}`}
                onClick={() => handleBoxClick("under_contrac_gcl_to_company_ytd")}
              />
              <LeadDataContainer
                text="AVG. COMMISSION PER TRANSACTION"
                number={`$${undercontractDashboard?.avg_commission_per_transaction_ytd ?? 0}`}
                onClick={() => handleBoxClick("avg_commission_per_transaction_ytd")}
              />

              <LeadDataContainer
                text="BUYER INTIAL OFFER TO UC CONVERSION RATE"
                number={undercontractDashboard?.buyer_initial_offer_to_under_contract_conversion_rate}
                onClick={() => handleBoxClick("buyer_initial_offer_to_under_contract_conversion_rate")}
              />
              <LeadDataContainer
                text="EXCLUSIVE TO UC CONVERSION RATE"
                number={undercontractDashboard?.exclusive_under_contract_conversion}
                onClick={() => handleBoxClick("exclusive_under_contract_conversion")}
              />
            </div>
          </div>
        </>
      )}

      <AddUnderContractModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />

      <EditUnderContractData
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
      />

      <ConvertClosedModal
        showModal={showConfirmModal}
        id={selectedId}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
      />

      <FellOutContract
        from="undercontact"
        showModal={showfelloutcontract}
        id={selectedId}
        onClose={() => setShowFellOutContract(false)}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />
    </BaseLayout>
  )
}

export default UnderContract
